import { paginatedQuery } from 'lib-shared-api-utils';

import { api } from 'app/app.api';

import { BaseAuditLogEntry, ResourceType } from './types/auditLogs.types';
import { ReadAuditLogsArg } from './types/auditLogsApi.types';
import { EnrichAuditLogEntry, EnrichmentType } from './types/enrichment.types';
import { enrichQueueAuditLogEntry } from './utils/enrichQueueAuditLogEntry';
import { transformAuditLogsResponse } from './utils/transformResponse';

export const auditLogsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    readConversationAuditLogs: builder.query(
      getReadAuditLogsEndpointDefinition('conversation'),
    ),
    readConversationAutomationAuditLogs: builder.query(
      getReadAuditLogsEndpointDefinition('automation'),
    ),
    readQueueAuditLogs: builder.query(
      getReadAuditLogsEndpointDefinition(
        'queue',
        ['users', 'teams'],
        enrichQueueAuditLogEntry,
      ),
    ),
    readConversationFlowAuditLogs: builder.query(
      getReadAuditLogsEndpointDefinition('conversation_through_flow'),
    ),
  }),
});

/**
 * Returns the endpoint definition for reading the audit logs of a single
 * resource. The resource is baked into the request path. We are flattening the
 * response's nested `event.payload` into the top-level entry for ease of use
 *
 * @param resourceType - The resource whose audit logs the endpoint reads.
 */
function getReadAuditLogsEndpointDefinition<
  TResource extends ResourceType,
  TEntry = BaseAuditLogEntry<TResource>,
>(
  resourceType: TResource,
  enrichments?: readonly EnrichmentType[],
  enrichEntry?: EnrichAuditLogEntry<TResource, TEntry>,
) {
  return {
    query: (arg: ReadAuditLogsArg<TResource>) =>
      paginatedQuery(
        arg,
        ({ resourceId, fromTs, toTs, eventTypes, authorIds }) => ({
          version: 1,
          path: `audit-logs/${resourceType}/${resourceId}`,
          params: {
            from_ts: fromTs,
            to_ts: toTs,
            event_type: eventTypes as string[],
            entry_author_id: authorIds,
            enrich: enrichments,
          },
        }),
      ),
    transformResponse: (response: any) =>
      transformAuditLogsResponse<TResource, TEntry>(response, enrichEntry),
  };
}
